import { Building2, ChevronDown, X } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { ROLE_COLORS, ROLE_LABELS } from './navConstants';

/**
 * Right-side context pills:
 *  • Static teal pill when a platform operator is in a non-provider tenant context
 *  • Provider org switcher (emerald) when in a provider tenant context
 *  • Demo role badge with switcher dropdown for seeded demo users
 *  • Plain role chip for everyone else
 */
export default function ContextIndicators({
  user, inContext, isProviderContext, operatorContext, handleResetContext,
  providerOrgs, selectProviderOrg, clearProviderOrg,
  isDemoRoleUser, switchingRole, handleDemoRoleSwitch,
}) {
  return (
    <>
      {inContext && !isProviderContext && (
        <div className="flex items-center gap-1.5 bg-teal-50 border border-teal-200 rounded-lg px-2.5 py-1" data-testid="context-indicator">
          <span className="text-[9px] uppercase tracking-wider text-teal-500 font-semibold leading-none">Impersonating</span>
          <span className="text-[11px] text-teal-700 font-medium max-w-[160px] truncate leading-none">{operatorContext.tenantName}</span>
          <span className="text-[9px] bg-teal-200/70 text-teal-700 font-semibold px-1 py-0.5 rounded leading-none uppercase">
            {operatorContext?.accountType === 'enterprise' ? 'V3' : 'V1'}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); handleResetContext?.(); }}
            className="ml-0.5 p-0.5 rounded hover:bg-teal-200/70 text-teal-500 hover:text-teal-700 transition-colors"
            title="Exit impersonation"
            data-testid="exit-impersonation-btn"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {isProviderContext && (
        <div className="flex items-center gap-1 bg-emerald-50 border border-emerald-300 rounded-lg pl-2.5 pr-1 py-1" data-testid="provider-context-pill">
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="provider-org-trigger"
            >
              <Building2 className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-[9px] uppercase tracking-wider text-emerald-500 font-semibold leading-none">Impersonating</span>
              <span className="text-[11px] text-emerald-700 font-medium max-w-[120px] truncate leading-none">{operatorContext.tenantName}</span>
              <span className="text-[9px] bg-emerald-200/70 text-emerald-700 font-semibold px-1 py-0.5 rounded leading-none uppercase">V2</span>
              <ChevronDown className="w-3 h-3 text-emerald-500" />
            </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="provider-org-dropdown">
            <div className="px-3 py-1.5 border-b">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Organizations</span>
            </div>
            <DropdownMenuItem
              onClick={clearProviderOrg}
              className={`cursor-pointer ${!operatorContext.orgId ? 'bg-emerald-50 font-medium' : ''}`}
              data-testid="provider-org-all"
            >
              <span className="text-sm">All Orgs</span>
              {!operatorContext.orgId && <div className="ml-auto w-2 h-2 rounded-full bg-emerald-500" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {providerOrgs.map(org => (
              <DropdownMenuItem
                key={org.org_id}
                onClick={() => selectProviderOrg(org)}
                className={`cursor-pointer ${operatorContext.orgId === org.org_id ? 'bg-emerald-50 font-medium' : ''}`}
                data-testid={`provider-org-${org.org_id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: org.branding_color || '#6366f1' }} />
                  <span className="text-sm truncate">{org.name}</span>
                </div>
                {operatorContext.orgId === org.org_id && <div className="ml-auto w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
              </DropdownMenuItem>
            ))}
            {providerOrgs.length === 0 && (
              <div className="px-3 py-3 text-center text-xs text-gray-400">No organizations</div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
          <button
            onClick={(e) => { e.stopPropagation(); handleResetContext?.(); }}
            className="p-0.5 rounded hover:bg-emerald-200/70 text-emerald-500 hover:text-emerald-700 transition-colors flex-shrink-0"
            title="Exit impersonation"
            data-testid="exit-impersonation-btn"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {isDemoRoleUser ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role]} hover:ring-2 hover:ring-offset-1 hover:ring-current/40 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1`}
            data-testid="user-role-badge"
            disabled={switchingRole}
            title="Switch demo role"
          >
            {switchingRole ? 'Switching…' : ROLE_LABELS[user.role]}
            <ChevronDown className="w-3 h-3 opacity-60" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="demo-role-switcher-menu">
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Switch Demo Role</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Logs you in as a different test user</p>
            </div>
            {['viewer', 'member', 'manager', 'admin'].map((r) => {
              const isCurrent = user.role === r;
              return (
                <DropdownMenuItem
                  key={r}
                  onClick={() => handleDemoRoleSwitch(r)}
                  disabled={isCurrent || switchingRole}
                  data-testid={`demo-role-switch-${r}`}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${r === 'viewer' ? 'bg-gray-400' : r === 'member' ? 'bg-green-500' : r === 'manager' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                    <span className="text-sm">{ROLE_LABELS[r]}</span>
                  </span>
                  {isCurrent && <span className="text-[10px] text-gray-400">Current</span>}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role]}`} data-testid="user-role-badge">
          {ROLE_LABELS[user.role]}
        </span>
      )}
    </>
  );
}
